import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { pnidApi } from '../api';
import { queryKeys } from '../queries/keys';
import { RegisterContext, RegisterEquipmentContext } from '../types';

export interface RegisterContextState {
  context: RegisterContext | null;
  loading: boolean;
  error: string | null;
}

/**
 * Drop the register's answer for a drawing and refetch it everywhere it is on
 * screen. A publish decides which drawing is primary, so the edit panel's field
 * locks are stale the moment one succeeds.
 */
export function useInvalidateRegisterContext(): (docId: string) => void {
  const queryClient = useQueryClient();
  return useCallback(
    (docId: string) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.registerContext(docId) });
    },
    [queryClient],
  );
}

/**
 * The register context for one drawing. Passing no drawing keeps the hook idle,
 * so a caller can mount it before it knows which drawing it is looking at.
 */
export function useRegisterContext(docId: string | null | undefined): RegisterContextState {
  const query = useQuery({
    queryKey: queryKeys.registerContext(docId ?? ''),
    queryFn: () => pnidApi.getRegisterContext(docId!),
    enabled: Boolean(docId),
  });

  return {
    context: query.data ?? null,
    loading: query.isPending && Boolean(docId),
    error: query.error
      ? ((query.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        || 'Failed to load register context')
      : null,
  };
}

/** The register's answer for one typed tag. */
export interface TagRegisterContextState {
  entry: RegisterEquipmentContext | null;
  loading: boolean;
  error: string | null;
}

/**
 * What the register knows about one tag on one drawing.
 *
 * The drawing-wide context answers for the equipment a drawing already draws;
 * this answers for a tag a reviewer has just settled on, which may belong to an
 * asset another drawing owns. Passing no tag keeps the hook idle, which is how
 * a caller says the reviewer has not changed the tag and the element's own
 * entry still answers.
 *
 * Each settled tag is read fresh — a reviewer typing a tag is choosing between
 * a handful of them, and a cache here would show a stale primacy right where
 * the answer has to be current.
 */
export function useTagRegisterContext(
  docId: string | null | undefined,
  tag: string | null,
): TagRegisterContextState {
  const [state, setState] = useState<TagRegisterContextState>({
    entry: null,
    loading: false,
    error: null,
  });

  useEffect(() => {
    if (!docId || !tag) {
      setState({ entry: null, loading: false, error: null });
      return;
    }
    const controller = new AbortController();
    setState({ entry: null, loading: true, error: null });
    pnidApi
      .getTagContext(docId, { tag, kind: 'equipment', signal: controller.signal })
      .then((entry) => {
        if (!controller.signal.aborted) setState({ entry, loading: false, error: null });
      })
      .catch((e: { response?: { data?: { detail?: string } } }) => {
        if (controller.signal.aborted) return;
        setState({
          entry: null,
          loading: false,
          error: e?.response?.data?.detail || 'Failed to load register context',
        });
      });
    return () => controller.abort();
  }, [docId, tag]);

  return state;
}

/** The register's record for one marked equipment, or null when it has no asset yet. */
export function getEquipmentRegisterContext(
  context: RegisterContext | null,
  equipmentId: string | null | undefined,
): RegisterEquipmentContext | null {
  if (!context || !equipmentId) return null;
  return context.equipments[equipmentId] ?? null;
}

/** The three answers to "is this drawing marked primary?", in the order they win. */
export interface PrimaryMarkSources {
  /**
   * The reviewer's own answer in the open dialog session, or null while they
   * have not touched the box.
   */
  liveToggle: boolean | null;
  /** The mark the dialog was seeded with from the drawing's loaded state. */
  wizardMark: boolean;
}

/**
 * Whether this drawing is marked primary for one register entry.
 *
 * A live toggle outranks everything: it is the newest statement a reviewer has
 * made and nothing has saved it yet. Otherwise the register answers, because it
 * is refetched — a sibling drawing that takes the asset over releases the mark
 * here, and only a reread of the register shows that release. Its per-drawing
 * mark is null for an entry this drawing draws no element for, as a tag a
 * reviewer has just typed, and then the drawing is primary only if the register
 * says so.
 *
 * The seeded mark answers only while the register has no entry at all, which is
 * equipment the register does not know yet.
 */
export function resolvePrimaryMark(
  entry: RegisterEquipmentContext | null,
  { liveToggle, wizardMark }: PrimaryMarkSources,
): boolean {
  if (liveToggle !== null) return liveToggle;
  if (!entry) return wizardMark;
  return entry.this_drawing_mark ?? entry.this_file_is_primary;
}

/**
 * Whether another drawing owns this asset's canonical fields.
 *
 * Type, sub-type and description describe the asset, not the drawing, so only
 * the drawing approved as its primary P&ID may state them. `isPrimaryHere` is
 * this drawing's own answer as `resolvePrimaryMark` reads it, so a reviewer who has
 * just claimed the asset unlocks the fields before the register hears of it.
 */
export function ownedByAnotherDrawing(
  entry: RegisterEquipmentContext | null,
  isPrimaryHere: boolean,
): boolean {
  return Boolean(
    entry && !entry.this_file_is_primary && entry.primary_source_file_id && !isPrimaryHere,
  );
}

/** The drawing that is primary for an asset, and where the asset sits on it. */
export interface PrimaryDrawing {
  isThisDrawing: boolean;
  drawingNo: string | null;
  docId: string | null;
  equipmentId: string | null;
}

/**
 * Which drawing is primary for one asset, or null while none is.
 *
 * `markedHere` is the mark on the equipment being edited right now, as
 * `resolvePrimaryMark` reads it. Passing it keeps this line and the checkbox
 * saying the same thing: a reviewer who has just ticked the box has named this
 * drawing before the register hears of it, and one who has not is shown
 * whatever the last read of the register said.
 */
export function primaryDrawing(
  entry: RegisterEquipmentContext,
  markedHere = false,
): PrimaryDrawing | null {
  if (markedHere || entry.this_file_is_primary) {
    return { isThisDrawing: true, drawingNo: null, docId: null, equipmentId: null };
  }
  if (!entry.primary_source_file_id) return null;
  const owner = entry.appearances.find(
    (appearance) => appearance.source_file_id === entry.primary_source_file_id,
  );
  return {
    isThisDrawing: false,
    drawingNo: owner?.drawing_no ?? null,
    docId: owner?.doc_id ?? null,
    equipmentId: owner?.equipment_id ?? null,
  };
}

/**
 * How the primary drawing reads to a person: this drawing, another drawing by
 * number, or none while no drawing is primary.
 */
export function describePrimaryDrawing(
  entry: RegisterEquipmentContext,
  markedHere = false,
): string {
  const primary = primaryDrawing(entry, markedHere);
  if (!primary) return 'none';
  if (primary.isThisDrawing) return 'this drawing';
  return primary.drawingNo || 'another drawing';
}
